import { FC, useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import MainContainer from '../../components/ui_components/MainContainer';
import {
  apiGetMyNotifications,
  apiMarkNotificationAsRead,
  apiDeleteNotification,
} from '../../components/ui_components/ApiList';
import colors from '../../styles/colors';
import type { Notification } from '../../types';
import { parseWrestlerTags } from '../../utils/wrestlerParser';

const POLL_INTERVAL_MS = 30000;

const PageWrapper = styled.div`
  width: 100%;
  padding: 16px;
`;

const PageTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid ${colors.bgColorBordeaux};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageCard = styled.li`
  background: ${colors.bgColorBlack2};
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 8px;
  overflow: hidden;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  cursor: pointer;
  flex-wrap: wrap;

  & .subject {
    flex: 1;
    min-width: 0;
    color: white;
    font-weight: 600;
    font-size: 14px;
  }

  & .meta {
    color: #aaa;
    font-size: 12px;
    flex-shrink: 0;
  }

  & .unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: gold;
    flex-shrink: 0;
  }

  & .actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  & .expand-icon {
    color: #aaa;
    font-size: 12px;
  }
`;

const MessageBody = styled.div`
  padding: 0 14px 14px;
  border-top: 1px solid rgba(246, 39, 83, 0.3);
`;

const MessageText = styled.p`
  color: #ddd;
  font-size: 13px;
  line-height: 1.5;
  margin: 10px 0 0;
  white-space: pre-wrap;
`;

const BtnDelete = styled.button`
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid ${colors.bgColorRed};
  border-radius: 6px;
  background: transparent;
  color: ${colors.bgColorRed};
  cursor: pointer;
`;

const EmptyState = styled.p`
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 32px 16px;
`;

const LoadingState = styled.p`
  color: #aaa;
  font-size: 14px;
  text-align: center;
  padding: 32px 16px;
`;

const ErrorState = styled.p`
  color: ${colors.bgColorRed};
  font-size: 14px;
  text-align: center;
  padding: 32px 16px;
`;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function senderLabel(from: Notification['from']): string {
  if (from === false || from == null) return 'Sistema';
  return from?.username ?? 'Sistema';
}

const MessaggiMobile: FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const loadInFlightRef = useRef(false);

  const loadNotifications = useCallback(async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    try {
      setError(null);
      const res = await apiGetMyNotifications();
      setNotifications(res.data ?? []);
    } catch (e) {
      setError('Impossibile caricare i messaggi.');
      setNotifications([]);
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const interval = setInterval(loadNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleToggleExpand = async (n: Notification) => {
    const isOpening = expandedId !== n.id;
    if (isOpening) {
      setExpandedId(n.id);
      if (n.read === null) {
        try {
          await apiMarkNotificationAsRead(n.id);
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === n.id ? { ...item, read: new Date().toISOString() } : item
            )
          );
        } catch {
          // ignore
        }
      }
    } else {
      setExpandedId(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo messaggio?')) return;
    try {
      await apiDeleteNotification(id);
      setNotifications((prev) => prev.filter((item) => item.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch {
      setError('Impossibile eliminare il messaggio.');
    }
  };

  return (
    <MainContainer>
      {loading && (
        <PageWrapper>
          <LoadingState>Caricamento messaggi...</LoadingState>
        </PageWrapper>
      )}
      {error && !loading && (
        <PageWrapper>
          <ErrorState>{error}</ErrorState>
        </PageWrapper>
      )}
      {!loading && !error && (
        <PageWrapper>
          <PageTitle>Messaggi</PageTitle>
          {notifications.length === 0 ? (
            <EmptyState>Nessun messaggio.</EmptyState>
          ) : (
            <List>
              {notifications.map((n) => (
                <MessageCard key={n.id}>
                  <MessageHeader onClick={() => handleToggleExpand(n)}>
                    <span className="subject">{parseWrestlerTags(n.subject, navigate, () => {})}</span>
                    <div className="actions">
                      {n.read === null && <span className="unread-dot" title="Non letto" />}
                      <span className="meta">
                        {senderLabel(n.from)} · {formatDate(n.created_at)}
                      </span>
                      <BtnDelete
                        type="button"
                        onClick={(e) => handleDelete(e, n.id)}
                        title="Elimina"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </BtnDelete>
                      <FontAwesomeIcon
                        className="expand-icon"
                        icon={expandedId === n.id ? faChevronUp : faChevronDown}
                      />
                    </div>
                  </MessageHeader>
                  {expandedId === n.id && (
                    <MessageBody>
                      <MessageText>{parseWrestlerTags(n.text, navigate, () => {})}</MessageText>
                    </MessageBody>
                  )}
                </MessageCard>
              ))}
            </List>
          )}
        </PageWrapper>
      )}
    </MainContainer>
  );
};

export default MessaggiMobile;
